import React, { useCallback, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import DayOrderEditorModal from "../APanel/CalendarPlus/DayOrderEditorModal";
import {
   fetchInstructors,
   updateInstructorOrder as persistInstructorOrder,
} from "../../store/instructorsSlice";
import { scheduleCalendarRefresh } from "../Utils/calendarBus";
import { formatInstructorOrderForSave } from "../../utils/instructorOrder";

const EMPTY_LIST = [];

export default function InstructorOrderEditorPopup({
   instructors = EMPTY_LIST,
   cars = EMPTY_LIST,
   title = "Ordinea instructorilor",
   onClose,
   onAlert,
}) {
   const dispatch = useDispatch();
   const [isSaving, setIsSaving] = useState(false);
   const submitRef = useRef(null);

   const registerSubmit = useCallback((fn) => {
      submitRef.current = typeof fn === "function" ? fn : null;
   }, []);

   const handleSave = useCallback(
      async (changes) => {
         const rows = Array.isArray(changes) ? changes : EMPTY_LIST;
         if (!rows.length) {
            onClose?.();
            return;
         }

         setIsSaving(true);
         try {
            for (const entry of rows) {
               const id = String(entry?.id ?? "").trim();
               const position = Math.max(
                  1,
                  Math.trunc(Number(entry?.position ?? entry?.order ?? 0)),
               );
               if (!id || !Number.isFinite(position)) continue;

               await dispatch(
                  persistInstructorOrder({
                     id,
                     order: formatInstructorOrderForSave(position),
                  }),
               ).unwrap();
            }

            await dispatch(fetchInstructors()).unwrap();
            scheduleCalendarRefresh({ type: "instructor-order-changed" });
            onAlert?.("success", "Ordinea instructorilor a fost salvata.");

            onClose?.();

            if (typeof window !== "undefined") {
               window.location.reload();
            }
         } catch (error) {
            onAlert?.(
               "error",
               error?.message || "Nu am putut salva ordinea instructorilor.",
            );
         } finally {
            setIsSaving(false);
         }
      },
      [dispatch, onAlert, onClose],
   );

   return (
      <section className="instructorOrderEditorPopup" aria-label={title}>
         <div className="instructorOrderEditorPopup__header">
            <h3 className="instructorOrderEditorPopup__title">{title}</h3>
            <p className="instructorOrderEditorPopup__subtitle">
               Aceeasi ordine pentru toate zilele. Trage instructorii sau
               adauga spatii goale.
            </p>
         </div>

         <div className="instructorOrderEditorPopup__content">
            <DayOrderEditorModal
               open={true}
               inline={false}
               layout="single"
               hideHeader
               hideDayHead
               hideActions
               registerSubmit={registerSubmit}
               instructors={instructors}
               cars={cars}
               isSavingExternal={isSaving}
               onClose={onClose}
               onSave={handleSave}
            />
         </div>

         <div className="instructorOrderEditorPopup__actions">
            <button
               type="button"
               className="instructorOrderEditorPopup__btn instructorOrderEditorPopup__btn--normal"
               onClick={onClose}
               disabled={isSaving}
            >
               Anuleaza
            </button>
            <button
               type="button"
               className="instructorOrderEditorPopup__btn instructorOrderEditorPopup__btn--save"
               onClick={() => submitRef.current?.()}
               disabled={isSaving}
            >
               {isSaving ? "Se salveaza..." : "Salveaza"}
            </button>
         </div>
      </section>
   );
}
